import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
import { fetchMbhData, fetchSpeakerStats } from '../api/client';
import CharacterImage from './CharacterImage';

const FILTERS = [
  { id: 'all',     label: 'All' },
  { id: 'pandava', label: 'Pandavas' },
  { id: 'kaurava', label: 'Kauravas' },
  { id: 'female',  label: 'Female' },
  { id: 'divine',  label: 'Divine' },
];

const PARENT_LABELS = {
  father:           'Father',
  mother:           'Mother',
  divineFather:     'Divine father',
  biologicalFather: 'Biological father',
  biologicalMother: 'Biological mother',
  fosterFather:     'Foster father',
  fosterMother:     'Foster mother',
  legalFather:      'Legal father',
};

function isFemale(p)  { return String(p?.gender || '').toLowerCase().startsWith('f'); }
function isDivine(p)  { return /deit|god/i.test(p?.role || ''); }
function personIcon(p) { return isDivine(p) ? '🪔' : isFemale(p) ? '👩' : '👤'; }

function portraitFallbackClass(p) {
  return `ft-hero-portrait-fallback ${isFemale(p) ? 'female' : isDivine(p) ? 'divine' : ''}`;
}

function personPortrait(p) {
  if (!p) return null;
  return personAvatar(p, 'ft-hero-portrait', portraitFallbackClass(p));
}

function personAvatar(p, className = '', fallbackClassName = '') {
  return (
    <CharacterImage
      key={p.id}
      name={p.name}
      sourceName={p.sourceName}
      alt={p.name}
      className={className}
      fallbackClassName={fallbackClassName}
      fallbackEmoji={personIcon(p)}
    />
  );
}

function PersonCard({ person, label, variant = '', onClick, disabled = false, staticCard = false }) {
  const className = [
    'ft-person-card',
    variant && `ft-person-card--${variant}`,
    staticCard && 'ft-person-card--static',
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {person ? personPortrait(person) : (
        <div className="ft-hero-portrait-fallback">👤</div>
      )}
      {label && <small>{label}</small>}
      <span>{person?.name || 'Unknown'}</span>
    </>
  );

  if (staticCard) {
    return <div className={className}>{content}</div>;
  }

  return (
    <button type="button" className={className} onClick={onClick} disabled={disabled}>
      {content}
    </button>
  );
}

function deriveRole(p) {
  if (p.role) return p.role;
  if (p.isPandava) return 'Pandava';
  if (p.isKaurava) return 'Kaurava';
  if (isDivine(p))  return 'Deity';
  return 'Mahabharata character';
}

function matchFilter(p, filter) {
  return filter === 'all'
    || (filter === 'pandava' && p.isPandava)
    || (filter === 'kaurava' && p.isKaurava)
    || (filter === 'female'  && isFemale(p))
    || (filter === 'divine'  && isDivine(p));
}

const FamilyTree = () => {
  const [data,       setData]       = useState(null);
  const [speakerMap, setSpeakerMap] = useState({});
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [selected,   setSelected]   = useState(null);
  const [filter,     setFilter]     = useState('all');
  const [query,      setQuery]      = useState('');
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  const [showParents,  setShowParents]  = useState(false);

  useEffect(() => {
    Promise.all([fetchMbhData(), fetchSpeakerStats()])
      .then(([d, stats]) => {
        setData(d);
        const map = {};
        stats.forEach(s => { if (s.speaker) map[s.speaker.toLowerCase()] = s.count; });
        setSpeakerMap(map);
        const first = d.narrative.find(x => x.personId)?.personId || d.people[0]?.id;
        setSelected(first);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const getPerson   = useCallback((id) => data?.people.find(p => p.id === id), [data]);
  const getFamilies = useCallback((id) => data?.families.filter(f => f.partners.includes(id)) || [], [data]);
  const getChildren = useCallback((fid) => (data?.children.filter(c => c.familyId === fid) || []).map(c => getPerson(c.child)).filter(Boolean), [data, getPerson]);
  const getParents  = useCallback((id) => data?.parentage.filter(x => x.child === id) || [], [data]);
  const getAllChildren = useCallback((id) => getFamilies(id).flatMap(f => getChildren(f.id)), [getFamilies, getChildren]);

  // Navigate via chip: auto-clear filter if destination is out of current filter
  const navigateTo = useCallback((id) => {
    if (!id || !data) return;
    const p = data.people.find(x => x.id === id);
    if (p && !matchFilter(p, filter)) setFilter('all');
    setSelected(id);
    setSidebarOpen(false);
    setShowParents(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [data, filter]);

  // Filter counts for pill labels
  const filterCounts = data ? {
    all:     data.people.length,
    pandava: data.people.filter(p => p.isPandava).length,
    kaurava: data.people.filter(p => p.isKaurava).length,
    female:  data.people.filter(p => isFemale(p)).length,
    divine:  data.people.filter(p => isDivine(p)).length,
  } : {};

  const filteredPeople = data?.people.filter(p => {
    const q = query.trim().toLowerCase();
    return matchFilter(p, filter) && (!q || `${p.name} ${p.role || ''}`.toLowerCase().includes(q));
  }) || [];

  const person       = getPerson(selected);
  const parents      = person ? getParents(person.id) : [];
  const allChildren  = person ? getAllChildren(person.id) : [];
  const narrativeIdx = data ? data.narrative.findIndex(x => x.personId === selected) : -1;
  const prev         = narrativeIdx > 0 ? data.narrative[narrativeIdx - 1] : null;
  const next         = narrativeIdx >= 0 && narrativeIdx < (data?.narrative.length || 0) - 1 ? data.narrative[narrativeIdx + 1] : null;
  const gitaCount    = person ? speakerMap[person.name.toLowerCase()] : 0;

  if (loading) return <div className="ft-loading"><div className="ft-loading-text">Loading family tree…</div></div>;
  if (error)   return <div className="ft-error">Could not load family data. Run <code>npm run sync-data</code> first.</div>;

  return (
    <div className="ft-root">

      {/* ── Mobile toggle bar ── */}
      <button className="ft-mobile-toggle" onClick={() => setSidebarOpen(o => !o)}>
        <Search size={14} aria-hidden="true" />
        <span>{filteredPeople.length} characters</span>
        {sidebarOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {/* ── Sidebar ── */}
      <aside className={`ft-sidebar ${sidebarOpen ? 'ft-sidebar--open' : ''}`}>
        <div className="ft-search-wrap">
          <Search className="ft-search-icon" size={15} aria-hidden="true" />
          <input
            type="text"
            className="ft-search-input"
            placeholder="Search characters…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        <div className="ft-filters">
          {FILTERS.map(f => (
            <button
              key={f.id}
              className={`ft-filter-pill ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              {filterCounts[f.id] != null && (
                <span className="ft-pill-count">{filterCounts[f.id]}</span>
              )}
            </button>
          ))}
        </div>

        <div className="ft-list-count">{filteredPeople.length} characters</div>

        <div className="ft-list">
          {filteredPeople.map(p => (
            <button
              key={p.id}
              className={`ft-person-btn ${p.id === selected ? 'active' : ''}`}
              onClick={() => { setSelected(p.id); setSidebarOpen(false); }}
            >
              {personAvatar(
                p,
                'ft-person-avatar-img',
                `ft-person-avatar ${isFemale(p) ? 'female' : isDivine(p) ? 'divine' : ''}`
              )}
              <span className="ft-person-info">
                <b className="ft-person-name">{p.name}</b>
                <small className="ft-person-role">{deriveRole(p)}</small>
              </span>
              {p.name && speakerMap[p.name.toLowerCase()] && (
                <span className="ft-gita-dot" title="Speaks in the Gita" />
              )}
            </button>
          ))}
          {filteredPeople.length === 0 && (
            <div className="ft-empty-list">No characters found.</div>
          )}
        </div>
      </aside>

      {/* ── Detail panel ── */}
      <section className="ft-detail">
        {!person ? (
          <div className="ft-empty-detail">Select a character to explore their family.</div>
        ) : (
          <>
            {/* Hero card */}
            <div className="ft-hero">
              <div className="ft-hero-top">
                {personPortrait(person)}
                <div className="ft-hero-body">
              <div className="ft-hero-eyebrow">Mahabharata · Family Tree</div>
              <h1 className="ft-hero-name">{person.name}</h1>
              <div className="ft-hero-role">{deriveRole(person)}</div>
              <div className="ft-hero-tags">
                {person.gender && <span className="ft-tag">{person.gender}</span>}
                {person.isPandava && <span className="ft-tag ft-tag--pandava">Pandava</span>}
                {person.isKaurava && <span className="ft-tag ft-tag--kaurava">Kaurava</span>}
                {isDivine(person) && <span className="ft-tag ft-tag--divine">Divine</span>}
                {gitaCount > 0 && (
                  <Link to="/speakers" className="ft-tag ft-tag--gita">
                    <BookOpen size={11} aria-hidden="true" />
                    Speaks {gitaCount} slokas in the Gita
                  </Link>
                )}
              </div>
              <div className="ft-hero-stats">
                <div className="ft-stat"><b>{getFamilies(person.id).length}</b><small>families</small></div>
                <div className="ft-stat"><b>{allChildren.length}</b><small>children</small></div>
                <div className="ft-stat"><b>{parents.length}</b><small>parent links</small></div>
              </div>
                </div>
              </div>
            </div>

            {/* Mini lineage tree */}
            {(parents.length > 0 || getFamilies(person.id).length > 0) && (
              <div className="ft-section">
                <div className="ft-section-head">
                  <h2 className="ft-section-title">Lineage</h2>
                </div>
                <div className="ft-mini-tree">
                  {/* Parent toggle — collapsed by default */}
                  {parents.length > 0 && (
                    <button
                      className={`ft-ancestry-btn ${showParents ? 'ft-ancestry-btn--open' : ''}`}
                      onClick={() => setShowParents(v => !v)}
                    >
                      <span className="ft-ancestry-arrow">{showParents ? '▾' : '▴'}</span>
                      {showParents ? 'Hide' : 'Show'} ancestry ({parents.length})
                    </button>
                  )}

                  {/* Parent row — shown when toggled */}
                  {parents.length > 0 && showParents && (() => {
                    const fatherTypes = new Set(['father','divineFather','biologicalFather','fosterFather','legalFather']);
                    const motherTypes = new Set(['mother','biologicalMother','fosterMother']);
                    const fathers = parents.filter(r => fatherTypes.has(r.type));
                    const mothers = parents.filter(r => motherTypes.has(r.type));
                    const others  = parents.filter(r => !fatherTypes.has(r.type) && !motherTypes.has(r.type));
                    const renderParentChip = (rel, key) => {
                      const par = getPerson(rel.parent);
                      return (
                        <PersonCard
                          key={key}
                          person={par}
                          label={PARENT_LABELS[rel.type] || rel.type}
                          variant="parent"
                          onClick={() => par && navigateTo(rel.parent)}
                          disabled={!par}
                        />
                      );
                    };
                    return (
                      <div className="ft-mini-row ft-mini-row--parents">
                        {mothers.length > 0 && (
                          <div className="ft-mini-parents-col">
                            {mothers.map((r, i) => renderParentChip(r, `m${i}`))}
                          </div>
                        )}
                        {mothers.length > 0 && fathers.length > 0 && <span className="ft-mini-heart">♥</span>}
                        {fathers.length > 0 && (
                          <div className="ft-mini-parents-col">
                            {fathers.map((r, i) => renderParentChip(r, `f${i}`))}
                          </div>
                        )}
                        {others.map((r, i) => renderParentChip(r, `o${i}`))}
                      </div>
                    );
                  })()}
                  {parents.length > 0 && showParents && <div className="ft-mini-vline" />}

                  {/* Family units side by side: each column = one union + its children */}
                  {getFamilies(person.id).length > 0 ? (
                    <div className="ft-mini-families-row">
                      {getFamilies(person.id).map(f => {
                        const partnerId = f.partners.find(x => x !== person.id);
                        const partner = getPerson(partnerId);
                        const kids = getChildren(f.id);
                        return (
                          <div key={f.id} className="ft-mini-family-col">
                            <div className="ft-mini-row ft-mini-row--self-spouses">
                              {partner && (
                                <>
                                  <PersonCard
                                    person={partner}
                                    label="Spouse"
                                    variant="spouse"
                                    onClick={() => navigateTo(partner.id)}
                                  />
                                  <span className="ft-mini-heart">♥</span>
                                </>
                              )}
                              <PersonCard person={person} label="Selected" variant="self" staticCard />
                            </div>
                            {kids.length > 0 && <div className="ft-mini-vline" />}
                            {kids.length > 0 && (
                              <div className="ft-mini-row ft-mini-row--children">
                                {kids.map(c => (
                                  <PersonCard
                                    key={c.id}
                                    person={c}
                                    label="Child"
                                    variant="child"
                                    onClick={() => navigateTo(c.id)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="ft-mini-row ft-mini-row--self-spouses">
                      <PersonCard person={person} label="Selected" variant="self" staticCard />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Marriages & unions */}
            {getFamilies(person.id).length > 0 && (
              <div className="ft-section">
                <div className="ft-section-head">
                  <h2 className="ft-section-title">Marriages &amp; unions</h2>
                  <span className="ft-section-badge">{getFamilies(person.id).length}</span>
                </div>
                <div className="ft-families-grid">
                  {getFamilies(person.id).map(f => {
                    const partnerId = f.partners.find(x => x !== person.id);
                    const partner   = getPerson(partnerId);
                    const kids      = getChildren(f.id);
                    return (
                      <div key={f.id} className="ft-family-card">
                        <div className="ft-family-header">
                          <small className="ft-family-type">{f.unionType}</small>
                          <div className="ft-family-pair">
                            {person.name} <span className="ft-heart">♥</span> {partner?.name || 'Unknown'}
                          </div>
                        </div>
                        <div className="ft-family-body">
                          <div className="ft-union-portraits">
                            <PersonCard person={person} label="Selected" variant="self" staticCard />
                            {partner && (
                              <>
                                <span className="ft-mini-heart ft-union-heart">♥</span>
                                <PersonCard
                                  person={partner}
                                  label="Spouse"
                                  variant="spouse"
                                  onClick={() => navigateTo(partner.id)}
                                />
                              </>
                            )}
                          </div>
                          {kids.length > 0 && (
                            <div className="ft-kids">
                              <div className="ft-kids-label">Children</div>
                              <div className="ft-kids-grid">
                                {kids.map(c => (
                                  <PersonCard
                                    key={c.id}
                                    person={c}
                                    label="Child"
                                    variant="child"
                                    onClick={() => navigateTo(c.id)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          {kids.length === 0 && <div className="ft-empty-kids">No children recorded.</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Parents & lineage detail */}
            {parents.length > 0 && (
              <div className="ft-section">
                <div className="ft-section-head">
                  <h2 className="ft-section-title">Parents &amp; lineage</h2>
                </div>
                <div className="ft-parents-grid">
                  {parents.map((rel, i) => {
                    const par = getPerson(rel.parent);
                    return (
                      <PersonCard
                        key={i}
                        person={par}
                        label={PARENT_LABELS[rel.type] || rel.type}
                        variant="parent"
                        onClick={() => par && navigateTo(rel.parent)}
                        disabled={!par}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Story position */}
            {narrativeIdx >= 0 && (
              <div className="ft-section">
                <div className="ft-section-head">
                  <h2 className="ft-section-title">Story position</h2>
                </div>
                <div className="ft-story-nav">
                  {prev?.personId
                    ? <button className="ft-story-btn" onClick={() => navigateTo(prev.personId)}><ChevronLeft size={15} /> {prev.name}</button>
                    : <div />}
                  <span className="ft-story-pos">{narrativeIdx + 1} / {data.narrative.length}</span>
                  {next?.personId
                    ? <button className="ft-story-btn ft-story-btn--next" onClick={() => navigateTo(next.personId)}>{next.name} <ChevronRight size={15} /></button>
                    : <div />}
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default FamilyTree;
